import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { simpleNoise } from './utils/noise.js';

/**
 * Klasa zarządzająca drogą z dynamicznym generowaniem segmentów
 * PROSTSZA WERSJA - używa Line2 do renderowania grubych linii
 * 
 * RÓŻNICE WZGLĘDEM perspective-road.js:
 * - Zamiast ręcznie tworzyć linie z TubeGeometry, używamy Line2/LineGeometry
 * - Ręczne tworzenie linii tylko poziomych i pionowych (bez przekątnych)
 * - Renderowanie jako Line2 zamiast grubych cylindrów (TubeGeometry)
 * - Mniej kodu (~600 linii vs ~1350 linii)
 * - Wsparcie dla grubych linii przez LineMaterial (lineWidth)
 * - Uproszczona obsługa załamań (tylko poziome)
 * 
 * ZALETY:
 * - Prostszy kod, łatwiejszy w utrzymaniu
 * - Obsługa grubości linii (LineMaterial.linewidth)
 * - Lepsza wydajność niż TubeGeometry (mniej geometrii)
 * 
 * WADY:
 * - Uproszczona obsługa załamań (tylko poziome)
 * - Wymaga aktualizacji resolution przy zmianie rozmiaru okna
 */
export class Road {
    constructor(config = {}) {
        this.config = {
            linesCount: config.linesCount || 10,
            startZ: config.horizonZ || config.startZ || -50, // Przesunięte do przodu (bliżej kamery)
            roadWidth: config.roadWidthAtBottom || config.roadWidth || 100,
            segmentSpacing: config.segmentSpacing || 20,
            cameraSpeed: config.cameraSpeed || 1.0,
            verticalRandomness: config.verticalRandomness || 0.5,
            blockNewSegments: config.blockNewSegments || false,
            lineWidth: config.lineWidth || 1.0,
            glowIntensity: config.glowIntensity || 1.0,
            lineBreaks: config.lineBreaks || {
                horizontal: false,
                vertical: false,
                position: 'random',
                strength: 1.0,
                count: 2,
                skipVerticalForBreaks: false
            }
        };
        
        this.group = new THREE.Group();
        this.lastAddedSegmentZ = null;
        
        // Macierz punktów w przestrzeni świata
        // [row][col] = {x, y, z}
        this.gridPoints = [];
        
        // Edges geometry (linie) - przechowujemy per segment
        this.segmentLines = new Map(); // Map<z, LineSegments[]> - linie per segment (używamy Z jako klucza)
        this.roadPlane = null;
        
        // Kolor bazowy - jasny turkusowy
        const baseColor = 0x15F8E5;
        const glowIntensity = this.config.glowIntensity || 1.0;
        const baseOpacity = 0.8;
        const bloomEmissiveIntensity = Math.max(glowIntensity * 2.0, 3.0);
        
        // Materiał dla linii krawędzi - użyj LineMaterial dla obsługi grubości linii
        // LineMaterial obsługuje lineWidth (w przeciwieństwie do LineBasicMaterial)
        const lineWidth = this.config.lineWidth || 1.0;
        const viewportWidth = window.visualViewport?.width || window.innerWidth || 1920;
        const viewportHeight = window.visualViewport?.height || window.innerHeight || 1080;
        this.lineMaterial = new LineMaterial({
            color: baseColor,
            linewidth: lineWidth, // Grubość linii w pikselach
            transparent: true,
            opacity: Math.min(baseOpacity * glowIntensity, 1.0),
            resolution: new THREE.Vector2(viewportWidth, viewportHeight) // Rozdzielczość viewportu
        });
    }
    
    /**
     * Inicjalizacja początkowej siatki
     */
    init() {
        this.generateInitialGrid();
        this.updateGroupPosition();
        this.group.renderOrder = 1;
        return this.group;
    }
    
    /**
     * Aktualizacja pozycji grupy
     */
    updateGroupPosition() {
        this.group.position.set(0, 0, 0);
    }
    
    /**
     * Generowanie początkowej siatki punktów
     * Używa addRoadSegment() do generowania segmentów - bez duplikacji kodu
     */
    generateInitialGrid() {
        const gridSize = 20;
        const segmentSpacing = this.config.segmentSpacing;
        const startZ = this.config.startZ;
        
        // Wygeneruj początkowe segmenty używając addRoadSegment()
        // Wywołujemy z forceZ aby wymusić konkretną pozycję Z (bez sprawdzania cameraZ)
        for (let row = 0; row < gridSize; row++) {
            const z = startZ + (row * segmentSpacing);
            this.addRoadSegment(null, z); // null = nie sprawdzaj cameraZ, użyj podanego Z
        }
        
        // Dodaj załamania jeśli włączone (tylko raz dla całej siatki)
        if (this.config.lineBreaks && (this.config.lineBreaks.horizontal || this.config.lineBreaks.vertical)) {
            this.addBreaksToGridPoints();
        }
        
        // Utwórz mesh z siatki i wykryj krawędzie (tylko raz po dodaniu wszystkich segmentów)
        this.createMeshFromGrid();
        
        // Utwórz płaszczyznę pod liniami
        this.createRoadPlane();
    }
    
    /**
     * Dodanie załamań do siatki (uproszczona wersja)
     */
    addBreaksToGridPoints() {
        const breaksConfig = this.config.lineBreaks;
        if (!breaksConfig || (!breaksConfig.horizontal && !breaksConfig.vertical)) {
            return;
        }
        
        // Uproszczona implementacja - dodaj załamania tylko poziome
        if (breaksConfig.horizontal && breaksConfig.count > 0) {
            const rows = this.gridPoints.length;
            const newGridPoints = [];
            
            for (let row = 0; row < rows; row++) {
                const newRow = [];
                const cols = this.gridPoints[row].length;
                
                for (let col = 0; col < cols; col++) {
                    const point = this.gridPoints[row][col];
                    newRow.push({ x: point.x, y: point.y, z: point.z, _hasDistortion: point._hasDistortion });
                    
                    if (col < cols - 1 && breaksConfig.horizontal) {
                        const nextPoint = this.gridPoints[row][col + 1];
                        const breaks = this.generateBreaksBetweenPoints(point, nextPoint, breaksConfig);
                        
                        for (let i = 1; i < breaks.length - 1; i++) {
                            newRow.push(breaks[i]);
                        }
                    }
                }
                
                newGridPoints.push(newRow);
            }
            
            this.gridPoints = newGridPoints;
        }
    }
    
    /**
     * Generowanie załamań między dwoma punktami
     */
    generateBreaksBetweenPoints(p1, p2, breaksConfig) {
        if (!breaksConfig || breaksConfig.count <= 0) {
            return [p1, p2];
        }
        
        const points = [p1];
        const count = breaksConfig.count;
        const strength = breaksConfig.strength !== undefined ? breaksConfig.strength : 1.0;
        const position = breaksConfig.position || 'random';
        
        const tValues = [];
        if (position === 'even') {
            for (let i = 1; i <= count; i++) {
                tValues.push(i / (count + 1));
            }
        } else {
            for (let i = 1; i <= count; i++) {
                const noisePos = simpleNoise(p1.x * 0.1 + i, p1.z * 0.1 + i);
                tValues.push(noisePos * 0.8 + 0.1);
            }
            tValues.sort((a, b) => a - b);
        }
        
        for (const t of tValues) {
            const x = p1.x + (p2.x - p1.x) * t;
            const z = p1.z + (p2.z - p1.z) * t;
            const baseY = p1.y + (p2.y - p1.y) * t;
            
            let y = baseY;
            if (strength > 0) {
                const breakNoise = simpleNoise(x * 0.15, z * 0.15);
                const yVariation = (breakNoise - 0.5) * 2 * strength;
                y = baseY + yVariation;
            }
            
            points.push({ x, y, z });
        }
        
        points.push(p2);
        return points;
    }
    
    /**
     * Utworzenie BufferGeometry z siatki punktów i wykrycie krawędzi
     * TYLKO linie poziome i pionowe - bez przekątnych w prostokątach
     * Używane tylko przy inicjalizacji - potem używamy addLinesForSegment()
     */
    createMeshFromGrid() {
        const rows = this.gridPoints.length;
        if (rows === 0) return;
        
        // Utwórz linie dla wszystkich segmentów (tylko przy inicjalizacji)
        for (let row = 0; row < rows; row++) {
            this.addLinesForSegment(row);
        }
    }
    
    /**
     * Dodanie linii tylko dla konkretnego segmentu (rowIndex)
     * Nie odbudowuje starych segmentów - zapobiega przeskakiwaniu
     * Używa Z jako klucza, bo rowIndex się zmienia gdy dodajemy nowe segmenty
     */
    addLinesForSegment(rowIndex) {
        const rows = this.gridPoints.length;
        if (rows === 0 || rowIndex >= rows) return;
        
        const cols = this.gridPoints[rowIndex] ? this.gridPoints[rowIndex].length : 0;
        if (cols === 0) return;
        
        // Pobierz Z pierwszego punktu w rzędzie jako unikalny identyfikator segmentu
        const segmentZ = this.gridPoints[rowIndex][0]?.z;
        if (segmentZ === undefined) return;
        
        // Jeśli linie dla tego segmentu już istnieją, usuń je najpierw
        if (this.segmentLines.has(segmentZ)) {
            const oldLines = this.segmentLines.get(segmentZ);
            oldLines.forEach(line => {
                this.group.remove(line);
                if (line.geometry) {
                    line.geometry.dispose();
                }
            });
        }
        
        const newLines = [];
        
        // Linie poziome dla tego segmentu - każda linia osobno
        for (let col = 0; col < cols - 1; col++) {
            const point1 = this.gridPoints[rowIndex][col];
            const point2 = this.gridPoints[rowIndex][col + 1];
            
            if (point1 && point1.x !== undefined && point2 && point2.x !== undefined) {
                // Utwórz osobny Line2 dla każdej linii poziomej
                const points = [point1.x, point1.y, point1.z, point2.x, point2.y, point2.z];
                const lineGeometry = new LineGeometry();
                lineGeometry.setPositions(points);
                
                const line2 = new Line2(lineGeometry, this.lineMaterial);
                line2.renderOrder = 1;
                line2.computeLineDistances();
                this.group.add(line2);
                newLines.push(line2);
            }
        }
        
        // Linie pionowe - tylko te, które zaczynają się w tym segmencie
        // (linie pionowe łączą punkty między rzędami, więc zaczynają się w rowIndex)
        if (rowIndex < rows - 1) {
            const nextCols = this.gridPoints[rowIndex + 1] ? this.gridPoints[rowIndex + 1].length : 0;
            const maxCols = Math.min(cols, nextCols);
            
            for (let col = 0; col < maxCols; col++) {
                const point1 = this.gridPoints[rowIndex][col];
                const point2 = this.gridPoints[rowIndex + 1][col];
                
                if (point1 && point1.x !== undefined && point2 && point2.x !== undefined) {
                    // Utwórz osobny Line2 dla każdej linii pionowej
                    const points = [point1.x, point1.y, point1.z, point2.x, point2.y, point2.z];
                    const lineGeometry = new LineGeometry();
                    lineGeometry.setPositions(points);
                    
                    const line2 = new Line2(lineGeometry, this.lineMaterial);
                    line2.renderOrder = 1;
                    line2.computeLineDistances();
                    this.group.add(line2);
                    newLines.push(line2);
                }
            }
        }
        
        // Zapisz linie dla tego segmentu używając Z jako klucza
        this.segmentLines.set(segmentZ, newLines);
    }
    
    /**
     * Utworzenie płaszczyzny pod liniami
     */
    createRoadPlane() {
        const rows = this.gridPoints.length;
        if (rows === 0) return;
        
        let minX = Infinity, maxX = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < this.gridPoints[row].length; col++) {
                const point = this.gridPoints[row][col];
                if (point) {
                    minX = Math.min(minX, point.x);
                    maxX = Math.max(maxX, point.x);
                    minZ = Math.min(minZ, point.z);
                    maxZ = Math.max(maxZ, point.z);
                }
            }
        }
        
        const margin = 0.5;
        const width = (maxX - minX) - margin * 2;
        const depth = (maxZ - minZ) - margin * 2;
        const centerX = (minX + maxX) / 2;
        const centerZ = (minZ + maxZ) / 2;
        
        const planeGeometry = new THREE.PlaneGeometry(Math.max(width, 0.1), Math.max(depth, 0.1), 1, 1);
        const planeMaterial = new THREE.MeshStandardMaterial({
            color: 0xFF0000,
            transparent: true,
            opacity: 0.95,
            side: THREE.DoubleSide,
            depthWrite: true,
            depthTest: true
        });
        
        this.roadPlane = new THREE.Mesh(planeGeometry, planeMaterial);
        this.roadPlane.position.set(centerX, -5.5, centerZ);
        this.roadPlane.rotation.x = -Math.PI / 2;
        this.roadPlane.renderOrder = 0;
        this.group.add(this.roadPlane);
    }
    
    /**
     * Aktualizacja drogi - zarządza segmentami
     */
    update(cameraZ) {
        this.addRoadSegment(cameraZ);
        this.removeRoadSegment(cameraZ);
    }
    
    /**
     * Dodanie nowego segmentu drogi z przodu
     * @param {number|null} cameraZ - Pozycja Z kamery (null = wymuś dodanie z forceZ)
     * @param {number|null} forceZ - Wymuś konkretną pozycję Z (używane przy inicjalizacji)
     */
    addRoadSegment(cameraZ, forceZ = null) {
        if (this.config.blockNewSegments && forceZ === null) {
            return;
        }
        
        const linesCount = this.config.linesCount;
        const segmentSpacing = this.config.segmentSpacing;
        const roadWidth = this.config.roadWidth;
        
        let needsNewSegment = false;
        let newZ = 0;
        
        // Jeśli forceZ jest podane, użyj go (dla inicjalizacji)
        if (forceZ !== null) {
            needsNewSegment = true;
            newZ = forceZ;
        } else if (this.gridPoints.length === 0) {
            // Jeśli siatka jest pusta, zacznij od startZ
            needsNewSegment = true;
            newZ = this.config.startZ;
        } else {
            // Sprawdź czy potrzebujemy nowego segmentu na podstawie cameraZ
            const firstRow = this.gridPoints[0];
            if (firstRow && firstRow.length > 0) {
                const firstZ = firstRow[0].z;
                const distanceToHorizon = cameraZ - firstZ;
                
                // Dodaj nowy segment gdy odległość do horyzontu jest mniejsza niż 50 (zamiast 100)
                if (distanceToHorizon < 150) {
                    const lastAddedZ = this.lastAddedSegmentZ || Infinity;
                    const proposedZ = firstZ - segmentSpacing;
                    
                    if (Math.abs(proposedZ - lastAddedZ) > 0.1) {
                        needsNewSegment = true;
                        newZ = proposedZ;
                        this.lastAddedSegmentZ = newZ;
                    }
                }
            } else {
                needsNewSegment = true;
                newZ = this.config.startZ;
            }
        }
        
        if (needsNewSegment) {
            const newRow = [];
            const width = roadWidth;
            
            for (let col = 0; col <= linesCount; col++) {
                const u = col / linesCount;
                const x = (u - 0.5) * 2 * width;
                const y = -5;
                newRow.push({ x, y, z: newZ });
            }
            
            this.gridPoints.unshift(newRow);
            
            // Ogranicz liczbę rzędów (tylko jeśli nie inicjalizujemy)
            if (forceZ === null) {
                const maxRows = 30;
                while (this.gridPoints.length > maxRows) {
                    this.gridPoints.pop();
                }
            }
            
            // Dodaj distortion
            this.addDistortionToNewSegment(newRow);
            
            // Dodaj linie tylko dla nowego segmentu (row 0) - nie odbudowuj starych!
            // To zapobiega przeskakiwaniu starych segmentów i jest znacznie wydajniejsze
            if (forceZ === null) {
                this.addLinesForSegment(0);
                // Aktualizuj płaszczyznę
                this.updateRoadPlane();
            }
        }
    }
    
    /**
     * Dodanie distortion do nowego segmentu
     */
    addDistortionToNewSegment(newRow) {
        if (this.config.verticalRandomness === 0) {
            return;
        }
        
        for (let col = 0; col < newRow.length; col++) {
            const point = newRow[col];
            if (point && !point._hasDistortion) {
                const noise = simpleNoise(point.x * 0.1, point.z * 0.1);
                const randomOffset = (noise - 0.5) * 2 * this.config.verticalRandomness;
                point.y = point.y + randomOffset;
                point._hasDistortion = true;
            }
        }
    }
    
    /**
     * Przebuduj mesh z całej siatki
     * Używane tylko przy inicjalizacji lub gdy zmieniamy konfigurację (linesCount, etc.)
     */
    rebuildMesh() {
        // Usuń wszystkie linie
        this.segmentLines.forEach((lines, z) => {
            lines.forEach(line => {
                this.group.remove(line);
                if (line.geometry) {
                    line.geometry.dispose();
                }
            });
        });
        this.segmentLines.clear();
        
        // Utwórz nowe linie dla wszystkich segmentów
        this.createMeshFromGrid();
        
        // Aktualizuj płaszczyznę
        if (this.roadPlane) {
            this.group.remove(this.roadPlane);
            if (this.roadPlane.geometry) {
                this.roadPlane.geometry.dispose();
            }
        }
        this.createRoadPlane();
    }
    
    /**
     * Aktualizacja płaszczyzny drogi po dodaniu/usunięciu segmentów
     */
    updateRoadPlane() {
        if (!this.roadPlane || !this.gridPoints || this.gridPoints.length === 0) {
            return;
        }
        
        let minX = Infinity, maxX = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        
        for (let row = 0; row < this.gridPoints.length; row++) {
            for (let col = 0; col < this.gridPoints[row].length; col++) {
                const point = this.gridPoints[row][col];
                if (point) {
                    minX = Math.min(minX, point.x);
                    maxX = Math.max(maxX, point.x);
                    minZ = Math.min(minZ, point.z);
                    maxZ = Math.max(maxZ, point.z);
                }
            }
        }
        
        const margin = 0.5;
        const width = (maxX - minX) - margin * 2;
        const depth = (maxZ - minZ) - margin * 2;
        const centerX = (minX + maxX) / 2;
        const centerZ = (minZ + maxZ) / 2;
        
        // Aktualizuj geometrię płaszczyzny
        this.roadPlane.geometry.dispose();
        this.roadPlane.geometry = new THREE.PlaneGeometry(
            Math.max(width, 0.1), 
            Math.max(depth, 0.1), 
            1, 
            1
        );
        
        // Aktualizuj pozycję
        this.roadPlane.position.set(centerX, -5.5, centerZ);
    }
    
    /**
     * Usunięcie segmentów drogi za kamerą
     */
    removeRoadSegment(cameraZ) {
        let removed = false;
        
        while (this.gridPoints.length > 0) {
            const lastRow = this.gridPoints[this.gridPoints.length - 1];
            if (lastRow && lastRow.length > 0) {
                const pointZ = lastRow[0].z;
                
                // Usuń segment gdy jest 20 jednostek za kamerą (zamiast 50) - wcześniejsze usuwanie
                if (pointZ > cameraZ ) {
                    // Usuń linie dla tego segmentu przed usunięciem z gridPoints
                    if (this.segmentLines.has(pointZ)) {
                        const lines = this.segmentLines.get(pointZ);
                        lines.forEach(line => {
                            this.group.remove(line);
                            if (line.geometry) {
                                line.geometry.dispose();
                            }
                        });
                        this.segmentLines.delete(pointZ);
                    }
                    
                    this.gridPoints.pop();
                    removed = true;
                } else {
                    break;
                }
            } else {
                break;
            }
        }
        
        if (removed) {
            // Aktualizuj płaszczyznę
            this.updateRoadPlane();
        }
    }
    
    /**
     * Aktualizacja konfiguracji w runtime
     */
    updateConfig(newConfig) {
        if (!newConfig) return;
        
        if (newConfig.linesCount !== undefined) this.config.linesCount = newConfig.linesCount;
        if (newConfig.lineWidth !== undefined) {
            this.config.lineWidth = newConfig.lineWidth;
            // Aktualizuj grubość linii w materiale
            if (this.lineMaterial && this.lineMaterial.linewidth !== undefined) {
                this.lineMaterial.linewidth = newConfig.lineWidth;
                this.lineMaterial.needsUpdate = true;
            }
        }
        if (newConfig.glowIntensity !== undefined) {
            this.config.glowIntensity = newConfig.glowIntensity;
            const glowIntensity = this.config.glowIntensity || 1.0;
            const baseOpacity = 0.8;
            if (this.lineMaterial) {
                this.lineMaterial.opacity = Math.min(baseOpacity * glowIntensity, 1.0);
                this.lineMaterial.needsUpdate = true;
            }
        }
        if (newConfig.verticalRandomness !== undefined) this.config.verticalRandomness = newConfig.verticalRandomness;
        if (newConfig.blockNewSegments !== undefined) this.config.blockNewSegments = newConfig.blockNewSegments;
        if (newConfig.lineBreaks) {
            Object.assign(this.config.lineBreaks, newConfig.lineBreaks);
        }
        
        // Przebuduj mesh jeśli potrzeba
        if (newConfig.linesCount !== undefined || newConfig.verticalRandomness !== undefined) {
            this.rebuildMesh();
        }
    }
    
    /**
     * Aktualizacja rozdzielczości dla LineMaterial (wymagane dla poprawnego renderowania)
     * Powinno być wywoływane przy zmianie rozmiaru okna
     */
    setResolution(width, height) {
        if (this.lineMaterial && this.lineMaterial.resolution) {
            this.lineMaterial.resolution.set(width, height);
            this.lineMaterial.needsUpdate = true;
        }
    }
    
    /**
     * Pobierz statystyki - liczba elementów na ekranie
     */
    getStats() {
        let totalLines = 0;
        this.segmentLines.forEach((lines) => {
            totalLines += lines.length;
        });
        
        const stats = {
            segments: this.gridPoints.length,
            lines: totalLines,
            points: this.gridPoints.reduce((sum, row) => sum + (row ? row.length : 0), 0)
        };
        
        return stats;
    }
    
    /**
     * Loguj statystyki do konsoli
     */
    logStats() {
        const stats = this.getStats();
        console.log(`[Road Stats] Segments: ${stats.segments}, Lines: ${stats.lines}, Points: ${stats.points}`);
        return stats;
    }
    
    /**
     * Cleanup resources
     */
    dispose() {
        // Usuń wszystkie linie
        this.segmentLines.forEach((lines, z) => {
            lines.forEach(line => {
                if (line.geometry) {
                    line.geometry.dispose();
                }
            });
        });
        this.segmentLines.clear();
        
        if (this.roadPlane && this.roadPlane.geometry) {
            this.roadPlane.geometry.dispose();
        }
        if (this.lineMaterial) {
            this.lineMaterial.dispose();
        }
        
        this.gridPoints = [];
    }
}



